import type { CanonicalOrderSnapshot } from '../order/order-finalization.ts';
import type { PaymentSnapshot } from '../payment/payment-authority.ts';
import type { FulfillmentSnapshot, SmtFulfillmentProjection } from '../fulfillment/fulfillment-authority.ts';
import type { PrintJobSnapshot, PrintJobStatus } from '../printing/print-authority.ts';
import type { StaffPermission } from '../staff/staff-authority.ts';
import type {
  SmtOrderActionViewModel,
  SmtOrderAttentionCode,
  SmtOrderDetailViewModel,
  SmtOrderLogicalPrintSummary,
  SmtOrderPaymentState,
  SmtOrdersFilter,
  SmtOrdersIntent,
  SmtOrdersListItemViewModel,
  SmtOrdersMutationResult,
  SmtOrdersReadRequest,
  SmtOrdersViewModel,
} from './smt-orders-contract.ts';

interface StaffAuthorizationInput {
  readonly actorId: string;
  readonly permission: StaffPermission;
  readonly at: string;
}

interface FulfillmentMutationInput {
  readonly expectedVersion: number;
  readonly idempotencyKey: string;
  readonly operatorId: string;
  readonly at: string;
}

interface RefundMutationInput extends FulfillmentMutationInput {
  readonly businessDate: string;
  readonly method: string;
  readonly amountMinor: number;
  readonly payerId?: string;
  readonly reference?: string;
  readonly reason: string;
}

interface CorrectionMutationInput extends RefundMutationInput {
  readonly direction: 'in' | 'out';
}

export interface SmtOrdersSurfaceDependencies {
  readonly listOrders: () => readonly CanonicalOrderSnapshot[];
  readonly paymentSnapshot: (orderId: string) => PaymentSnapshot;
  readonly fulfillmentSnapshot: (orderId: string) => FulfillmentSnapshot;
  readonly listPrintJobs: (orderId: string) => Promise<readonly PrintJobSnapshot[]>;
  readonly projectFulfillment: (snapshot: FulfillmentSnapshot) => SmtFulfillmentProjection;
  readonly requiredFlowCanCheckout: (snapshot: FulfillmentSnapshot) => boolean;
  readonly authorizeStaff?: (input: StaffAuthorizationInput) => void;
  readonly authorizeProduction?: (orderId: string, command: FulfillmentMutationInput) => unknown;
  readonly markReady?: (orderId: string, command: FulfillmentMutationInput) => unknown;
  readonly markCompleted?: (orderId: string, command: FulfillmentMutationInput) => unknown;
  readonly recordRefund?: (orderId: string, command: RefundMutationInput) => unknown;
  readonly recordCorrection?: (orderId: string, command: CorrectionMutationInput) => unknown;
}

type ComposedOrder = {
  readonly item: SmtOrdersListItemViewModel;
  readonly detail: SmtOrderDetailViewModel;
};

const clone = <T>(value: T): T => structuredClone(value);
const errorCode = (error: unknown): string => error instanceof Error ? error.message : 'SMT_ORDERS_UNKNOWN_ERROR';

const assertReadRequest = (request: SmtOrdersReadRequest): void => {
  if (!request.actorId?.trim()) throw new Error('SMT_ORDERS_ACTOR_REQUIRED');
  if (!Number.isFinite(Date.parse(request.at))) throw new Error('SMT_ORDERS_TIMESTAMP_INVALID');
  const { createdFrom, createdTo } = request.filter ?? {};
  if (createdFrom !== undefined && !Number.isFinite(Date.parse(createdFrom))) throw new Error('SMT_ORDERS_FILTER_TIME_INVALID');
  if (createdTo !== undefined && !Number.isFinite(Date.parse(createdTo))) throw new Error('SMT_ORDERS_FILTER_TIME_INVALID');
  if (createdFrom !== undefined && createdTo !== undefined && Date.parse(createdFrom) > Date.parse(createdTo)) {
    throw new Error('SMT_ORDERS_FILTER_RANGE_INVALID');
  }
};

const paymentState = (payment: PaymentSnapshot): SmtOrderPaymentState => {
  if (payment.fullySettled) return 'settled';
  if (payment.summary.creditMinor > 0) return 'credit';
  if (payment.summary.netReceivedMinor > 0) return 'partial';
  return 'unsettled';
};

const summarizePrint = (
  jobs: readonly PrintJobSnapshot[],
  queryErrorCode?: string,
): SmtOrderLogicalPrintSummary => {
  if (queryErrorCode) {
    return {
      state: 'unknown',
      jobCount: 0,
      acceptedCount: 0,
      failedCount: 0,
      unknownCount: 0,
      errorCode: queryErrorCode,
      intents: [],
    };
  }
  if (jobs.length === 0) {
    return {
      state: 'not_requested',
      jobCount: 0,
      acceptedCount: 0,
      failedCount: 0,
      unknownCount: 0,
      intents: [],
    };
  }
  const statuses = jobs.map((job) => job.status);
  let state: PrintJobStatus;
  if (statuses.includes('unknown')) state = 'unknown';
  else if (statuses.includes('failed')) state = 'failed';
  else if (statuses.includes('dispatching')) state = 'dispatching';
  else if (statuses.includes('queued')) state = 'queued';
  else state = 'accepted';
  return {
    state,
    jobCount: jobs.length,
    acceptedCount: jobs.filter((job) => job.status === 'accepted').length,
    failedCount: jobs.filter((job) => job.status === 'failed').length,
    unknownCount: jobs.filter((job) => job.status === 'unknown').length,
    intents: jobs.map((job) => ({ intentId: job.intentId, status: job.status, dedupeKey: job.dedupeKey })),
  };
};

const attentionFor = (
  payment: PaymentSnapshot,
  fulfillment: FulfillmentSnapshot,
  logicalPrint: SmtOrderLogicalPrintSummary,
): readonly SmtOrderAttentionCode[] => {
  const attention: SmtOrderAttentionCode[] = [];
  if (payment.summary.outstandingMinor > 0) attention.push('payment.outstanding');
  if (payment.summary.creditMinor > 0) attention.push('payment.credit');
  if (fulfillment.state === 'cancelled') attention.push('fulfillment.cancelled');
  if (logicalPrint.state === 'failed') attention.push('print.failed');
  if (logicalPrint.state === 'unknown') attention.push('print.unknown');
  return attention;
};

const matchesFilter = (item: SmtOrdersListItemViewModel, filter: SmtOrdersFilter | undefined): boolean => {
  if (!filter) return true;
  const query = filter.identityQuery?.trim().toLowerCase();
  if (query && !item.orderId.toLowerCase().includes(query)) return false;
  if (filter.source?.length && !filter.source.includes(item.source)) return false;
  if (filter.channel?.length && !filter.channel.includes(item.channel)) return false;
  if (filter.status?.length && !filter.status.includes(item.fulfillment.smt.stage)) return false;
  if (filter.createdFrom !== undefined && Date.parse(item.createdAt) < Date.parse(filter.createdFrom)) return false;
  if (filter.createdTo !== undefined && Date.parse(item.createdAt) > Date.parse(filter.createdTo)) return false;
  return true;
};

export function createSmtOrdersSurface(deps: SmtOrdersSurfaceDependencies) {
  const permissionReason = (actorId: string, permission: StaffPermission, at: string): string | null => {
    if (!deps.authorizeStaff) return 'STAFF_AUTHORITY_UNAVAILABLE';
    try {
      deps.authorizeStaff({ actorId, permission, at });
      return null;
    } catch (error) {
      return errorCode(error);
    }
  };

  const actionsFor = (
    order: CanonicalOrderSnapshot,
    payment: PaymentSnapshot,
    fulfillment: FulfillmentSnapshot,
    request: SmtOrdersReadRequest,
  ): readonly SmtOrderActionViewModel[] => {
    let authorizeReason: string | null = null;
    if (fulfillment.state !== 'held') authorizeReason = 'FULFILLMENT_TRANSITION_INVALID';
    else if (!deps.requiredFlowCanCheckout(fulfillment)) authorizeReason = 'FULFILLMENT_REQUIRED_FLOW_INCOMPLETE';
    else if (fulfillment.requiresPaymentBeforeProduction && !payment.fullySettled) authorizeReason = 'FULFILLMENT_PAYMENT_REQUIRED';
    else if (!deps.authorizeProduction) authorizeReason = 'SMT_ORDERS_ACTION_UNAVAILABLE';

    const readyReason = fulfillment.state !== 'production_authorized'
      ? 'FULFILLMENT_TRANSITION_INVALID'
      : !deps.markReady ? 'SMT_ORDERS_ACTION_UNAVAILABLE' : null;
    const completeReason = fulfillment.state !== 'ready'
      ? 'FULFILLMENT_TRANSITION_INVALID'
      : !deps.markCompleted ? 'SMT_ORDERS_ACTION_UNAVAILABLE' : null;

    const refundPermission = permissionReason(request.actorId, 'payment.refund', request.at);
    const refundReason = refundPermission
      ?? (payment.summary.refundableMinor <= 0
        ? 'PAYMENT_OUTFLOW_EXCEEDS_REFUNDABLE'
        : !deps.recordRefund ? 'SMT_ORDERS_ACTION_UNAVAILABLE' : null);
    const correctionPermission = permissionReason(request.actorId, 'payment.correction', request.at);
    const correctionReason = correctionPermission ?? (!deps.recordCorrection ? 'SMT_ORDERS_ACTION_UNAVAILABLE' : null);

    return [
      {
        actionId: 'fulfillment.authorize-production',
        label: '開始製作',
        enabled: authorizeReason === null,
        disabledReason: authorizeReason,
        permission: null,
        requiredOrderRevision: order.revision,
        requiredFulfillmentVersion: fulfillment.version,
      },
      {
        actionId: 'fulfillment.mark-ready',
        label: '標記可取',
        enabled: readyReason === null,
        disabledReason: readyReason,
        permission: null,
        requiredOrderRevision: order.revision,
        requiredFulfillmentVersion: fulfillment.version,
      },
      {
        actionId: 'fulfillment.mark-completed',
        label: '完成訂單',
        enabled: completeReason === null,
        disabledReason: completeReason,
        permission: null,
        requiredOrderRevision: order.revision,
        requiredFulfillmentVersion: fulfillment.version,
      },
      {
        actionId: 'payment.refund',
        label: '退款',
        enabled: refundReason === null,
        disabledReason: refundReason,
        permission: 'payment.refund',
        requiredOrderRevision: order.revision,
        requiredPaymentVersion: payment.version,
      },
      {
        actionId: 'payment.correction',
        label: '付款更正',
        enabled: correctionReason === null,
        disabledReason: correctionReason,
        permission: 'payment.correction',
        requiredOrderRevision: order.revision,
        requiredPaymentVersion: payment.version,
      },
    ];
  };

  const compose = async (order: CanonicalOrderSnapshot, request: SmtOrdersReadRequest): Promise<ComposedOrder> => {
    const payment = deps.paymentSnapshot(order.orderId);
    if (payment.orderId !== order.orderId || payment.orderRevision !== order.revision) {
      throw new Error('SMT_ORDERS_PAYMENT_ORDER_MISMATCH');
    }
    const fulfillment = deps.fulfillmentSnapshot(order.orderId);
    if (fulfillment.orderId !== order.orderId || fulfillment.orderRevision !== order.revision) {
      throw new Error('SMT_ORDERS_FULFILLMENT_ORDER_MISMATCH');
    }

    let printJobs: readonly PrintJobSnapshot[] = [];
    let printError: string | undefined;
    try {
      printJobs = await deps.listPrintJobs(order.orderId);
    } catch (error) {
      printError = errorCode(error);
    }
    const logicalPrint = summarizePrint(printJobs, printError);
    const projectedFulfillment = deps.projectFulfillment(fulfillment);
    const attention = attentionFor(payment, fulfillment, logicalPrint);
    const provenance = {
      orderRevision: order.revision,
      paymentVersion: payment.version,
      fulfillmentVersion: fulfillment.version,
      authority: clone(order.authority),
      printIntentIds: logicalPrint.intents.map((intent) => intent.intentId),
    } as const;

    const item: SmtOrdersListItemViewModel = {
      orderId: order.orderId,
      orderIdLabel: order.orderId,
      orderRevision: order.revision,
      businessDate: order.businessDate,
      source: order.source,
      channel: order.channel,
      serviceMode: order.serviceMode,
      createdAt: order.createdAt,
      acceptedAt: null,
      updatedAt: null,
      itemCount: order.lines.reduce((sum, line) => sum + line.quantity, 0),
      currency: order.currency,
      totalMinor: order.totalMinor,
      payment: {
        state: paymentState(payment),
        version: payment.version,
        fullySettled: payment.fullySettled,
        summary: clone(payment.summary),
      },
      fulfillment: {
        state: fulfillment.state,
        version: fulfillment.version,
        smt: clone(projectedFulfillment),
      },
      logicalPrint,
      attention,
      provenance,
    };

    const detail: SmtOrderDetailViewModel = {
      order: clone(order),
      payment: clone(payment),
      fulfillment: clone(fulfillment),
      logicalPrint: clone(logicalPrint),
      attention: [...attention],
      actions: actionsFor(order, payment, fulfillment, request),
      provenance: clone(provenance),
    };
    return { item, detail };
  };

  const snapshot = async (request: SmtOrdersReadRequest): Promise<SmtOrdersViewModel> => {
    assertReadRequest(request);
    const composed = await Promise.all(deps.listOrders().map((order) => compose(order, request)));
    composed.sort((left, right) => {
      const byTime = Date.parse(right.item.createdAt) - Date.parse(left.item.createdAt);
      return byTime !== 0 ? byTime : left.item.orderId.localeCompare(right.item.orderId);
    });

    let selectedOrder: SmtOrderDetailViewModel | null = null;
    if (request.selectedOrderId !== undefined) {
      const selected = composed.find((entry) => entry.item.orderId === request.selectedOrderId);
      if (!selected) throw new Error('SMT_ORDERS_ORDER_NOT_FOUND');
      selectedOrder = selected.detail;
    }

    return {
      items: composed.filter((entry) => matchesFilter(entry.item, request.filter)).map((entry) => entry.item),
      selectedOrderId: request.selectedOrderId ?? null,
      selectedOrder,
    };
  };

  const dispatch = async (
    intent: SmtOrdersIntent,
    viewRequest: SmtOrdersReadRequest,
  ): Promise<SmtOrdersMutationResult> => {
    const responseRequest: SmtOrdersReadRequest = {
      ...viewRequest,
      actorId: intent.actorId,
      at: intent.at,
      selectedOrderId: intent.orderId,
    };
    const refreshedRejectionSnapshot = async (): Promise<SmtOrdersViewModel> => {
      try {
        return await snapshot(responseRequest);
      } catch (error) {
        if (errorCode(error) !== 'SMT_ORDERS_ORDER_NOT_FOUND') throw error;
        return snapshot({ ...responseRequest, selectedOrderId: undefined });
      }
    };
    const reject = async (code: string): Promise<SmtOrdersMutationResult> => ({
      kind: 'rejected',
      actionId: intent.actionId,
      code,
      snapshot: await refreshedRejectionSnapshot(),
    });

    if (viewRequest.actorId !== intent.actorId) return reject('SMT_ORDERS_ACTOR_MISMATCH');

    let before: SmtOrdersViewModel;
    try {
      before = await snapshot(responseRequest);
    } catch (error) {
      const code = errorCode(error);
      if (code !== 'SMT_ORDERS_ORDER_NOT_FOUND') throw error;
      return reject(code);
    }

    const action = before.selectedOrder?.actions.find((candidate) => candidate.actionId === intent.actionId);
    if (!action) return reject('SMT_ORDERS_ACTION_NOT_AVAILABLE');
    if (action.requiredOrderRevision !== intent.expectedOrderRevision) {
      return reject('SMT_ORDERS_ORDER_REVISION_CONFLICT');
    }
    if ('expectedFulfillmentVersion' in intent && action.requiredFulfillmentVersion !== intent.expectedFulfillmentVersion) {
      return reject('FULFILLMENT_VERSION_CONFLICT');
    }
    if ('expectedPaymentVersion' in intent && action.requiredPaymentVersion !== intent.expectedPaymentVersion) {
      return reject('PAYMENT_VERSION_CONFLICT');
    }
    if (!action.enabled) return reject(action.disabledReason ?? 'SMT_ORDERS_ACTION_DISABLED');

    try {
      switch (intent.actionId) {
        case 'fulfillment.authorize-production':
          deps.authorizeProduction?.(intent.orderId, {
            expectedVersion: intent.expectedFulfillmentVersion,
            idempotencyKey: intent.idempotencyKey,
            operatorId: intent.actorId,
            at: intent.at,
          });
          break;
        case 'fulfillment.mark-ready':
          deps.markReady?.(intent.orderId, {
            expectedVersion: intent.expectedFulfillmentVersion,
            idempotencyKey: intent.idempotencyKey,
            operatorId: intent.actorId,
            at: intent.at,
          });
          break;
        case 'fulfillment.mark-completed':
          deps.markCompleted?.(intent.orderId, {
            expectedVersion: intent.expectedFulfillmentVersion,
            idempotencyKey: intent.idempotencyKey,
            operatorId: intent.actorId,
            at: intent.at,
          });
          break;
        case 'payment.refund':
          deps.recordRefund?.(intent.orderId, {
            expectedVersion: intent.expectedPaymentVersion,
            idempotencyKey: intent.idempotencyKey,
            operatorId: intent.actorId,
            at: intent.at,
            businessDate: intent.businessDate,
            method: intent.method,
            amountMinor: intent.amountMinor,
            payerId: intent.payerId,
            reference: intent.reference,
            reason: intent.reason,
          });
          break;
        case 'payment.correction':
          deps.recordCorrection?.(intent.orderId, {
            expectedVersion: intent.expectedPaymentVersion,
            idempotencyKey: intent.idempotencyKey,
            operatorId: intent.actorId,
            at: intent.at,
            businessDate: intent.businessDate,
            direction: intent.direction,
            method: intent.method,
            amountMinor: intent.amountMinor,
            payerId: intent.payerId,
            reference: intent.reference,
            reason: intent.reason,
          });
          break;
      }
      return { kind: 'accepted', actionId: intent.actionId, snapshot: await snapshot(responseRequest) };
    } catch (error) {
      return reject(errorCode(error));
    }
  };

  return { snapshot, dispatch };
}
